import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  detectElement,
  detectDamage,
  removeBackground,
  detectOrientation,
  readCarteGrise,
  getAllProcessing,
  getCarProcessedById,
  generateReportCorrection,
  getNextDataToReport,
  updateCarProcessedToModerate,
  getKpiMoteur,
  getKpiEvolution,
  getKpiMoteurElement,
  getKpiMoteurDamage,
  getKPIElementDamageLink,
  getKPIElementDamageLevelCricity,
  getKPIEvolutionElement,
  getKPIEvolutionDamage,
  getKPIEvolutionElementDamageLink,
  getAllComponentCarKey,
  getAllDefectCarKey,
  updateProcessing,
  uploadProcessingImage,
  deleteProcessingImage,
  updateFirestoreImage,
  createManyProcessings,
  createManyFirestoreImages,
  deleteProcessing,
  deleteFirestoreImageByImageUrl,
  getAllProcessingsByElementClassId,
} from '@/api/ai_model/ai_model';
import { PageRequest, Processing } from '@/api/entities/processing.ts';
import { EngineCorrectionReportDataType } from '@/api/entities/engineCorrection';
import { KpiFilterRequest } from '@/api/entities/kpi';
import { FirestoreImage } from '@/api/entities/ia';
import { TCustomQueryProps } from '@/type/hooks.types';
import { ErrorResponse } from '@/utils/axios.http';
import { notify } from '@/utils/toast.utils';
import { NOTIFY } from '@/utils/constant.utils';

export const useCreateManyProcessings = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Processing[]) => createManyProcessings(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['allProcessing'] });

      notify(
        variables.length > 1
          ? t('common:imagesUploadedSuccessfully')
          : t('common:imageUploadedSuccessfully'),
        NOTIFY.SUCCESS
      );

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useDeleteProcessing = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteProcessing(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allProcessing'] });

      notify(t('common:imageDeletedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useDetectionElement = () => {
  return useMutation({
    mutationFn: (file: File) => detectElement(file),
    onSuccess: () => {},
    onError: (error) => {
      console.error('API call failed with error:', error);
    },
  });
};

export const useDetectionDamage = () => {
  return useMutation({
    mutationFn: (file: File) => detectDamage(file),
    onSuccess: () => {},
    onError: (error) => {
      console.error('API call failed with error:', error);
    },
  });
};

export const useDetectionOrientation = () => {
  return useMutation({
    mutationFn: (file: File) => detectOrientation(file),
    onSuccess: () => {},
    onError: (error) => {
      console.error('API call failed with error:', error);
    },
  });
};

export const useRemoveImageBackground = () => {
  return useMutation({
    mutationFn: (file: File) => removeBackground(file),
    onSuccess: () => {},
    onError: (error) => {
      console.error('API call failed with error:', error);
    },
  });
};

export const useReadCarteGrise = () => {
  return useMutation({
    mutationFn: (file: File) => readCarteGrise(file),
    onSuccess: () => {},
    onError: (error) => {
      console.error('API call failed with error:', error);
    },
  });
};

export const useGetAllProcessing = (params: PageRequest) => {
  return useQuery({
    queryKey: ['allProcessing', params],
    queryFn: () => getAllProcessing(params),
  });
};

export const useGetCarProcessedById = (id: string) => {
  return useQuery({
    queryKey: ['getCarProcessedById', id],
    queryFn: () => getCarProcessedById(id),
  });
};

export const useGetAllProcessingsByElementClassId = (
  relatedTo: 'detection' | 'moderation',
  classId: number,
  page: number,
  limit: number
) => {
  return useQuery({
    queryKey: [
      'getAllProcessingsByElementClassId',
      relatedTo,
      classId,
      page,
      limit,
    ],
    queryFn: () =>
      getAllProcessingsByElementClassId(relatedTo, classId, page, limit),
  });
};

export const useGenerateReportEngine = () => {
  return useMutation({
    mutationFn: (reportData: EngineCorrectionReportDataType) =>
      generateReportCorrection(reportData),
    onSuccess: () => {},
    onError: (error) => {
      console.error('error when report engine correction', error);
    },
  });
};

export const useGetNextDataReport = (id: string) => {
  return useQuery({
    queryKey: ['getNextDataReport', id],
    queryFn: () => getNextDataToReport(id),
  });
};

export const useUpdateCarProcessedToModerate = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      correctionElNb,
      correctionDmgNb,
      detectionsElements,
      detectionsDamagesDetected,
      moderatedClassesElements,
      moderationsElements,
      moderatedClassesDamages,
      moderationsDamages,
    }: {
      id: string;
      correctionElNb: number;
      correctionDmgNb: number;
      detectionsElements?: object[];
      detectionsDamagesDetected?: object[];
      moderatedClassesElements?: string[];
      moderationsElements?: object[];
      moderatedClassesDamages?: string[];
      moderationsDamages?: object[];
    }) =>
      updateCarProcessedToModerate(
        id,
        correctionElNb,
        correctionDmgNb,
        detectionsElements,
        detectionsDamagesDetected,
        moderatedClassesElements,
        moderationsElements,
        moderatedClassesDamages,
        moderationsDamages
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['allProcessing'],
      });

      queryClient.invalidateQueries({
        queryKey: ['getCarProcessedById'],
      });

      queryClient.invalidateQueries({
        queryKey: ['getAllProcessingsByElementClassId'],
      });

      if (onSuccess) onSuccess();
    },
    onError: (error) => {
      console.error('error when update report', error);
    },
  });
};

export const useUpdateProcessing = ({
  t,
  showDefaultSuccessMessage,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (processing: Processing) => updateProcessing(processing),
    onSuccess: () => {
      // const { id } = variables;

      // queryClient.invalidateQueries({ queryKey: ['getCarProcessedById', id] });
      queryClient.invalidateQueries({ queryKey: ['getCarProcessedById'] });
      queryClient.invalidateQueries({ queryKey: ['allProcessing'] });

      if (showDefaultSuccessMessage) {
        notify(t('common:updateSuccessful'), NOTIFY.SUCCESS);
      }

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useUploadProcessingImage = ({
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  return useMutation({
    mutationFn: (files: File[]) => uploadProcessingImage(files),
    onSuccess: (data) => {
      handleSuccess?.(data);
    },
    onError: (error: ErrorResponse) => {
      console.error(error.error);
    },
  });
};

export const useDeleteProcessingImage = ({
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  return useMutation({
    mutationFn: (fileName: string) => deleteProcessingImage(fileName),
    onSuccess: () => {
      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      console.error('Error when deleting image:', error);
    },
  });
};

export const useCreateManyFirestoreImages = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: FirestoreImage[]) => createManyFirestoreImages(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allProcessing'] });

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useUpdateFirestoreImage = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  return useMutation({
    mutationFn: (data: FirestoreImage) => updateFirestoreImage(data),
    onSuccess: () => {
      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useDeleteFirestoreImageByImageUrl = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  return useMutation({
    mutationFn: (imageUrl: string) => deleteFirestoreImageByImageUrl(imageUrl),
    onSuccess: () => {
      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useGetKPIMoteur = (params: KpiFilterRequest) => {
  return useQuery({
    queryKey: ['kpiMoteur', params],
    queryFn: () => getKpiMoteur(params),
  });
};

export const useGetKPIMoteurElement = (params: KpiFilterRequest) => {
  return useQuery({
    queryKey: ['KPIMoteurElement', params],
    queryFn: () => getKpiMoteurElement(params),
  });
};

export const useGetKPIMoteurDamage = (params: KpiFilterRequest) => {
  return useQuery({
    queryKey: ['KPIMoteurDamage', params],
    queryFn: () => getKpiMoteurDamage(params),
  });
};

export const useGetKPIElementDamageLink = (params: KpiFilterRequest) => {
  return useQuery({
    queryKey: ['KPIElementDamageLink', params],
    queryFn: () => getKPIElementDamageLink(params),
  });
};

export const useGetKPIElementDamageLevelCricity = (
  params: KpiFilterRequest
) => {
  return useQuery({
    queryKey: ['KPIElementDamageLevelCricity', params],
    queryFn: () => getKPIElementDamageLevelCricity(params),
  });
};

export const useGetKPIEvolution = (params: KpiFilterRequest) => {
  return useQuery({
    queryKey: ['kpiEvolution', params],
    queryFn: () => getKpiEvolution(params),
  });
};

export const useGetKPIEvolutionElement = (params: KpiFilterRequest) => {
  return useQuery({
    queryKey: ['KPIEvolutionElement', params],
    queryFn: () => getKPIEvolutionElement(params),
  });
};

export const useGetKPIEvolutionDamage = (params: KpiFilterRequest) => {
  return useQuery({
    queryKey: ['KPIEvolutionDamage', params],
    queryFn: () => getKPIEvolutionDamage(params),
  });
};

export const useGetKPIEvolutionElementDamageLink = (
  params: KpiFilterRequest
) => {
  return useQuery({
    queryKey: ['KPIEvolutionElementDamageLink', params],
    queryFn: () => getKPIEvolutionElementDamageLink(params),
  });
};

export const useGetAllComponentKey = () => {
  return useQuery({
    queryKey: ['componentKey'],
    queryFn: () => getAllComponentCarKey(),
  });
};

export const useGetAllDefectKey = () => {
  return useQuery({
    queryKey: ['defectKey'],
    queryFn: () => getAllDefectCarKey(),
  });
};
